import styled from 'styled-components'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useEffect } from 'react'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { Body, Headline } from '@shipt/design-system-typography'
import { type HeaderBannerData } from '@/features/cms/components/CMS/types'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { screenSizes } from '@shipt/design-system-themes'
import { Column } from '@shipt/design-system-layouts'
import { Image } from '@/features/shared/elements/Image/NextImage'

/**
 * This component corresponds to the `header_banner` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/header_banner
 * - **Production** - https://cms.shipt.com/content-type/pool/header_banner
 */
export const HeaderBanner = ({ id, content_type_id, data }: HeaderBannerData) => {
  const {
    heading,
    desktop_headline_placement,
    subheading,
    heading_surface,
    background_image,
    mobile_background_image,
  } = data

  const { trackCMSElementViewed } = useCMSTrackingEvents()

  const hasOverlayHeading = !!background_image && desktop_headline_placement === 'overlay'

  useEffect(() => {
    trackCMSElementViewed({
      content: 'Header banner',
    })
  }, [trackCMSElementViewed])

  return (
    <SkeletonConsumer height={200}>
      {!!background_image && (
        <HeroLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
          <MobileBannerImage
            src={mobile_background_image?.src ?? background_image.src}
            alt={mobile_background_image?.alt ?? background_image.alt}
            fill
            sizes="720px"
            preload
          />

          <BannerImage
            src={background_image.src}
            alt={background_image.alt}
            fill
            sizes="1280px"
            preload
            className="fullWidth"
          />

          {desktop_headline_placement === 'overlay' && (
            <TextContainer justify="center" spacing="md">
              <Headline surface={heading_surface} size="xl" as="h1">
                {heading}
              </Headline>
              {!!subheading && (
                <Body surface={heading_surface} size={{ base: 'lg', md: 'xl' }}>
                  {subheading}
                </Body>
              )}
            </TextContainer>
          )}
        </HeroLayoutContainer>
      )}
      <BottomTextContainer placement={desktop_headline_placement} noBackground={!background_image}>
        <Column spacing="xs">
          <Headline surface={heading_surface} size="xl" as={hasOverlayHeading ? 'p' : 'h1'}>
            {heading}
          </Headline>
          {!!subheading && (
            <Body surface={heading_surface} size={{ base: 'lg', md: 'xl' }}>
              {subheading}
            </Body>
          )}
        </Column>
      </BottomTextContainer>
    </SkeletonConsumer>
  )
}

const HeroLayoutContainer = styled(GridLayoutContainer)`
  height: 12.5rem;
  position: relative;

  @media ${screenSizes.max_mobile} {
    --padding-inline: 0;
  }

  @media ${screenSizes.only_tablet} {
    --content-max-width: 1280px;
  }

  @media ${screenSizes.smDesktop} {
    max-width: 1080px;
    border-radius: 8px;
    overflow: hidden;
    margin: 0 auto;
  }
`

const MobileBannerImage = styled(Image)`
  object-fit: cover;
  object-position: center;

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const BannerImage = styled(Image)`
  object-fit: cover;

  @media ${screenSizes.max_mobile} {
    display: none;
  }
`

const TextContainer = styled(Column)`
  position: absolute;
  z-index: 2;
  top: 4.75rem;
  display: none;

  @media ${screenSizes.only_tablet} {
    top: 5rem;
  }

  @media ${screenSizes.tablet} {
    display: block;
  }
`

const BottomTextContainer = styled(GridLayoutContainer)<{
  placement: HeaderBannerData['data']['desktop_headline_placement']
  noBackground: boolean
}>`
  padding: ${({ noBackground }) => (noBackground ? '32px 0' : '12px 0')};

  @media ${screenSizes.only_tablet} {
    --content-max-width: 1280px;
  }

  @media ${screenSizes.tablet} {
    display: ${({ placement, noBackground }) => placement === 'overlay' && !noBackground && 'none'};
  }

  @media ${screenSizes.smDesktop} {
    padding: ${({ noBackground }) => noBackground && '8px 0 32px'};
  }
`
